import { APIConfiguration } from '@savgroup-front-common/configuration';

const SESSION_PROXY_PREFIX = '/api';

export const SESSION_REQUEST_HEADER_NAME = 'X-Requested-With';
export const SESSION_REQUEST_HEADER_VALUE = 'fetch';

const UNAUTHORIZED_STATUS = 401;

const sessionUnauthorizedListeners = new Set<() => void>();

let isSessionCookieModeEnabled = false;

export function enableSessionCookieMode(): void {
  isSessionCookieModeEnabled = true;
}

export function getIsSessionCookieModeEnabled(): boolean {
  return isSessionCookieModeEnabled;
}

export function toSameOriginApiUrl(url: string): string {
  const servicesBase = APIConfiguration.base.replace(/\/$/, '');

  if (!servicesBase || !url.startsWith(`${servicesBase}/`)) {
    return url;
  }

  return `${SESSION_PROXY_PREFIX}${url.slice(servicesBase.length)}`;
}

export function buildSessionRequestHeaders(
  url: string,
): Record<string, string> {
  if (!url.startsWith(`${SESSION_PROXY_PREFIX}/`)) {
    return {};
  }

  return { [SESSION_REQUEST_HEADER_NAME]: SESSION_REQUEST_HEADER_VALUE };
}

export function onSessionUnauthorized(listener: () => void): () => void {
  sessionUnauthorizedListeners.add(listener);

  return () => {
    sessionUnauthorizedListeners.delete(listener);
  };
}

interface ReportSessionResponseArgs {
  url: string;
  status: number;
}

export function reportSessionResponse({
  url,
  status,
}: ReportSessionResponseArgs): void {
  if (
    status !== UNAUTHORIZED_STATUS ||
    !url.startsWith(`${SESSION_PROXY_PREFIX}/`)
  ) {
    return;
  }

  sessionUnauthorizedListeners.forEach((listener) => listener());
}

function getRequestedPath(input: RequestInfo | URL): string {
  if (typeof input === 'string') {
    return input;
  }

  const url = new URL(input instanceof Request ? input.url : input);

  return url.origin === globalThis.location?.origin ? url.pathname : url.href;
}

export async function fetchWithSessionReport(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const response = await fetch(input, init);

  reportSessionResponse({
    url: getRequestedPath(input),
    status: response.status,
  });

  return response;
}

export function resetSessionCookieModeForTests(): void {
  if (!import.meta.env.DEV) {
    return;
  }

  isSessionCookieModeEnabled = false;
  sessionUnauthorizedListeners.clear();
}
